<template>
  <main class="life-atlas">
    <div class="atlas-topline atlas-shell">
      <span class="atlas-wordmark"><BiographyStar class="atlas-spark" /> THE HUMAN ARCHIVE</span>
    </div>

    <header class="atlas-hero atlas-shell">
      <div class="atlas-hero-copy">
        <p class="atlas-kicker">
          SELF BIOGRAPHY / PEOPLE & THEIR STORIES
        </p>
        <h1>Every life.<br>A whole <em>world.</em></h1>
        <p class="atlas-deck">
          Behind every name, a life you haven't discovered.<br class="desktop-break"> Step into the memories, ideas, and moments that make us who we are.
        </p>
        <a
          class="atlas-button atlas-button-dark"
          href="#explore-heading"
        >Find your next story <span aria-hidden="true">↗</span></a>
        <div class="atlas-hero-note">
          <span class="atlas-note-line"></span> A collection of lives, told by the people who live them.
        </div>
      </div>
      <div
        class="atlas-collage"
        aria-label="A glimpse into Pepe Moreno's biography"
      >
        <div
          class="atlas-orbit"
          aria-hidden="true"
        ></div>
        <router-link
          v-if="featured"
          :to="profileLink(featured)"
          class="atlas-photo atlas-photo-main"
          aria-label="Explore Pepe Moreno's biography"
        >
          <div class="atlas-photo-window">
            <BiographyCover
              :profile="featured"
              prefer-avatar
              loading="eager"
            />
          </div>
          <span>Pepe Moreno <span aria-hidden="true">↗</span></span>
        </router-link>
        <div
          v-else
          class="atlas-photo atlas-photo-main atlas-photo-placeholder"
          aria-hidden="true"
        >
          <img
            :src="defaultAvatar"
            alt=""
          >
        </div>
        <div
          class="atlas-photo atlas-photo-memory"
          aria-hidden="true"
        >
          <div class="atlas-photo-window">
            <BiographyCover
              v-if="featured && featured.cover_url"
              :profile="featured"
              loading="eager"
            /><img
              v-else
              :src="defaultAvatar"
              alt=""
            >
          </div>
          <span>Pieces of a life.</span>
        </div>
        <div
          class="atlas-stamp"
          aria-hidden="true"
        >
          <span>ONE LIFE.</span><b>∞</b><span>ENDLESS STORIES.</span>
        </div>
      </div>
    </header>

    <div
      class="atlas-ribbon"
      aria-hidden="true"
    >
      <span>MEMORIES</span><BiographyStar /><span>PEOPLE</span><BiographyStar /><span>IDEAS</span><BiographyStar /><span>PLACES</span><BiographyStar /><span>A LIFE'S WORK</span><BiographyStar /><span>CONNECTIONS</span>
    </div>

    <section
      v-if="featured"
      class="atlas-feature atlas-shell"
      aria-labelledby="atlas-feature-title"
    >
      <div class="atlas-section-label">
        <span>01 / IN THE SPOTLIGHT</span><span>A LIFE, UP CLOSE</span>
      </div>
      <div class="atlas-feature-heading">
        <h2 id="atlas-feature-title">
          Featured <em>Biographies</em>
        </h2><BiographyStar class="atlas-outline-star" />
      </div>
      <router-link
        :to="profileLink(featured)"
        class="atlas-feature-story"
      >
        <div class="atlas-feature-image">
          <BiographyCover
            :profile="featured"
            prefer-avatar
            loading="eager"
          />
        </div>
        <div
          class="atlas-feature-shade"
          aria-hidden="true"
        ></div>
        <div
          class="atlas-feature-index"
          aria-hidden="true"
        >
          01
        </div>
        <div class="atlas-feature-content">
          <span class="atlas-feature-tag">THE FEATURED LIFE</span>
          <h3>Pepe<br><em>Moreno.</em></h3>
          <p>{{ featuredLine }}</p>
          <span class="atlas-button atlas-button-paper">Enter his world <span aria-hidden="true">↗</span></span>
        </div>
        <span class="atlas-feature-credit">ART. MEMORY. A LIFE IN CREATION.</span>
      </router-link>
    </section>

    <section
      class="atlas-discovery atlas-shell"
      aria-labelledby="explore-heading"
      :aria-busy="String(loading)"
    >
      <div class="atlas-section-label">
        <span>02 / THE COLLECTION</span><span>EVERY STORY STARTS SOMEWHERE</span>
      </div>
      <div class="atlas-discovery-heading">
        <h2 id="explore-heading">
          Meet your next<br><em>discovery.</em>
        </h2>
        <p>A familiar name. A different perspective.<br>A whole life waiting to be explored.</p>
      </div>
      <form
        class="atlas-search"
        role="search"
        @submit.prevent="searchProfiles"
      >
        <label class="atlas-search-field"><v-icon size="22">{{ icons.mdiMagnify }}</v-icon><span class="atlas-sr-only">Search biographies</span><input
          v-model="search"
          type="search"
          placeholder="Find a name. Discover a story."
          maxlength="100"
        ></label>
        <button
          type="submit"
          :disabled="loading"
          class="atlas-search-submit"
        >
          Search <span aria-hidden="true">↗</span>
        </button>
        <label class="atlas-sort"><span class="atlas-sr-only">Sort biographies</span><select
          v-model="sort"
          @change="searchProfiles"
        ><option value="newest">Newest profiles</option><option value="name">Name A to Z</option></select></label>
      </form>
      <div
        v-if="error"
        class="atlas-state"
        role="alert"
      >
        <h3>A little pause in the story.</h3><p>{{ error }}</p><button
          class="atlas-button atlas-button-dark"
          @click="loadProfiles"
        >
          Try again
        </button>
      </div>
      <div
        v-else-if="loading"
        class="atlas-loading"
        role="status"
      >
        <BiographyStar class="atlas-spark" /> Gathering the stories…
      </div>
      <template v-else-if="users.length">
        <div class="atlas-result-note">
          <span>{{ appliedSearch ? `RESULTS FOR “${appliedSearch}”` : 'OPEN A LIFE. FIND A CONNECTION.' }}</span><span>PAGE {{ String(page).padStart(2, '0') }}</span>
        </div>
        <div class="atlas-gallery">
          <router-link
            v-for="(user, index) in users"
            :key="user.id"
            :to="profileLink(user)"
            class="atlas-person"
            :class="{ 'atlas-person-symbolic': isSymbolic(user) }"
            :style="{ '--poster-color': posterColor(user) }"
          >
            <div class="atlas-person-image">
              <BiographyCover
                :profile="user"
                @source-change="setCoverState(user, $event)"
              />
            </div>
            <div
              v-if="isSymbolic(user)"
              class="atlas-person-initials"
              aria-hidden="true"
            >
              {{ initials(user) }}
            </div>
            <div
              class="atlas-person-gradient"
              aria-hidden="true"
            ></div>
            <span class="atlas-person-number">{{ String((page - 1) * 12 + index + 1).padStart(2, '0') }}</span>
            <span
              class="atlas-person-open"
              aria-hidden="true"
            >↗</span>
            <div class="atlas-person-copy">
              <span class="atlas-person-eyebrow">A LIFE IN WORDS & PICTURES</span><h3>{{ user.name || user.username }}</h3><span class="atlas-person-handle">@{{ user.username }}</span><p v-if="user.bio">
                {{ excerpt(user.bio) }}
              </p>
            </div>
          </router-link>
        </div>
        <nav
          v-if="page > 1 || hasMore"
          class="atlas-pagination"
          aria-label="Biography pages"
        >
          <button
            :disabled="page === 1"
            @click="changePage(-1)"
          >
            <span aria-hidden="true">←</span> Previous
          </button><span aria-live="polite">{{ String(page).padStart(2, '0') }}</span><button
            :disabled="!hasMore"
            @click="changePage(1)"
          >
            More lives to discover <span aria-hidden="true">→</span>
          </button>
        </nav>
      </template>
      <div
        v-else
        class="atlas-state"
        role="status"
      >
        <h3>{{ appliedSearch ? 'A story still to be found.' : 'The next chapter is on its way.' }}</h3><p>{{ appliedSearch ? 'Try a different name or username.' : 'Public biographies will appear here as people share their stories.' }}</p><button
          v-if="appliedSearch"
          class="atlas-button atlas-button-dark"
          @click="clearSearch"
        >
          Explore all biographies
        </button>
      </div>
    </section>

    <aside class="atlas-closing">
      <div class="atlas-shell">
        <span class="atlas-kicker">THE STORY CONTINUES</span><p>No two lives alike.<br><em>Every one worth knowing.</em></p><a href="#explore-heading">Keep exploring <span aria-hidden="true">↑</span></a><BiographyStar class="atlas-closing-art" />
      </div>
    </aside>
  </main>
</template>

<script>
import Biographies from './Biographies.vue'
import BiographyCover from '@/components/BiographyCover.vue'
import BiographyStar from '@/components/BiographyStar.vue'

export default {
  name: 'BiographiesVisual',
  components: { BiographyCover, BiographyStar },
  extends: Biographies,
  data: () => ({ fallbackCovers: {} }),
  computed: {
    featuredLine() {
      const text = this.excerpt(this.featured && this.featured.bio)

      return text ? (text.match(/^[\s\S]*?[.!?](?:\s|$)/) || [text])[0].trim() : 'Discover the memories, ideas, and experiences behind a creative life.'
    },
  },
  methods: {
    isSymbolic(user) {
      return (!user.cover_url && !user.avatar) || this.fallbackCovers[user.id]
    },
    setCoverState(user, state) {
      this.$set(this.fallbackCovers, user.id, state.fallback)
    },
    initials(user) {
      return (user.name || user.username).trim().split(/\s+/).slice(0, 2).map(word => word[0])
        .join('')
        .toUpperCase()
    },
    posterColor(user) {
      const colors = ['#66557b', '#42645b', '#8a5646', '#405b78', '#756047', '#605780']

      return colors[Number(user.id) % colors.length]
    },
  },
}
</script>

<style scoped>
.life-atlas { --paper: #f4f1e9; --ink: #252925; --muted: #64685e; --line: #d4d4c9; --accent: #7757b7; background: var(--paper); color: var(--ink); overflow: hidden; font-family: Inter, Arial, sans-serif; }
.atlas-shell { width: min(1320px, calc(100% - 96px)); margin: 0 auto; }
.atlas-topline { display: flex; justify-content: space-between; align-items: center; padding: 28px 0 24px; border-bottom: 1px solid var(--line); }
.atlas-wordmark { font-size: 11px; letter-spacing: .18em; display: flex; align-items: center; gap: 10px; font-weight: 700; }
.atlas-spark { font-size: 32px; line-height: 1; color: var(--accent); }
.atlas-hero { display: grid; grid-template-columns: 1.15fr 1fr; gap: 30px; align-items: center; min-height: 680px; padding-top: 60px; padding-bottom: 70px; }
.atlas-kicker { font-size: 10px; font-weight: 700; letter-spacing: .2em; margin-bottom: 25px; color: var(--muted); }
.atlas-hero h1 { font-family: Georgia, 'Times New Roman', serif; font-weight: 400; font-size: clamp(58px, 7.5vw, 108px); line-height: .98; letter-spacing: -.065em; color: var(--ink); margin: 0 0 30px; }
.life-atlas em { font-family: Georgia, 'Times New Roman', serif; font-weight: 400; }
.atlas-hero h1 em { color: var(--accent); }
.atlas-deck { max-width: 420px; font-size: 15px; line-height: 1.8; color: var(--muted); margin-bottom: 28px; }
.atlas-button { display: inline-flex; justify-content: space-between; gap: 32px; align-items: center; padding: 17px 22px; font-size: 12px; font-weight: 600; border-radius: 2px; text-decoration: none; transition: transform .2s; }
.atlas-button span { font-size: 24px; line-height: 1; }
.atlas-button-dark { background: var(--ink); color: var(--paper) !important; }
.atlas-button-paper { background: #f4f1e9; color: #252925 !important; }
.atlas-button:hover { transform: translateY(-3px); }
.atlas-hero-note { display: flex; align-items: center; gap: 12px; max-width: 350px; margin-top: 36px; font-size: 10px; line-height: 1.7; color: var(--muted); }
.atlas-note-line { width: 36px; height: 1px; flex-shrink: 0; background: var(--muted); }
.atlas-collage { position: relative; height: 500px; }
.atlas-orbit { position: absolute; width: 90%; height: 90%; border: 1px solid var(--line); border-radius: 50%; top: 4%; left: 6%; }
.atlas-photo { position: absolute; padding: 10px 10px 0; background: #fffdf6; box-shadow: 0 20px 45px #24211826; text-decoration: none; color: #383b34 !important; }
.atlas-photo-window { position: relative; width: 100%; overflow: hidden; }
.atlas-photo-window > img { width: 100%; height: 100%; object-fit: cover; }
.atlas-photo > span { display: flex; align-items: center; justify-content: space-between; padding: 14px 4px; font-family: Georgia, serif; font-style: italic; font-size: 15px; }
.atlas-photo-main { width: 62%; left: 4%; top: 1%; transform: rotate(-8deg); z-index: 1; }
.atlas-photo-main .atlas-photo-window { aspect-ratio: 4 / 5; }
.atlas-photo-placeholder img { width: 100%; }
.atlas-photo-memory { width: 54%; right: -1%; bottom: 9%; transform: rotate(9deg); z-index: 2; }
.atlas-photo-memory .atlas-photo-window { aspect-ratio: 1.25; }
.atlas-collage-caption { position: absolute; right: 1%; top: 5%; writing-mode: vertical-rl; font-size: 9px; letter-spacing: .22em; }
.atlas-collage-foot { position: absolute; left: 3%; bottom: 1%; font-size: 9px; letter-spacing: .19em; }
.atlas-stamp { position: absolute; right: 0; top: 4%; z-index: 3; width: 118px; height: 118px; border-radius: 50%; background: #cfdf83; color: #283b27; display: flex; flex-direction: column; align-items: center; justify-content: center; transform: rotate(13deg); box-shadow: 0 4px 15px #00000012; }
.atlas-stamp span { font-size: 8px; letter-spacing: .1em; font-weight: 700; }
.atlas-stamp b { font-family: Georgia, serif; font-size: 46px; font-weight: 400; line-height: 1.2; }
.atlas-ribbon { display: flex; gap: 40px; justify-content: center; align-items: center; overflow: hidden; white-space: nowrap; padding: 21px 0; background: #c4b0e2; color: #332346; font-size: 10px; letter-spacing: .18em; font-weight: 700; }
.atlas-ribbon .biography-star { font-size: 19px; }
.atlas-feature { padding-top: 72px; }
.atlas-section-label { display: flex; align-items: center; justify-content: space-between; font-size: 9px; letter-spacing: .18em; font-weight: 600; padding: 0 0 18px; border-bottom: 1px solid var(--line); color: var(--muted); }
.atlas-feature-heading { display: flex; align-items: center; justify-content: space-between; padding: 28px 0 32px; }
.atlas-feature-heading h2 { font-size: clamp(27px, 3.3vw, 46px); font-weight: 400; letter-spacing: -.04em; }
.atlas-outline-star { font-size: 46px; color: var(--accent); }
.atlas-feature-story { display: block; position: relative; min-height: 640px; background: #1d2924; color: #fff !important; text-decoration: none; overflow: hidden; filter: sepia(.9) saturate(.7) contrast(.95); }
.atlas-feature-image { position: absolute; inset: 0 0 0 27%; }
.atlas-feature-shade { position: absolute; inset: 0; background: linear-gradient(90deg, #172620 0%, #172620e8 24%, #17262060 60%, #17262000 84%), linear-gradient(0deg, #101f18b3, transparent 40%); }
.atlas-feature-index { position: absolute; top: 28px; right: 32px; font-family: Georgia, serif; font-size: 80px; line-height: 1; color: #ffffffb3; }
.atlas-feature-content { position: relative; max-width: 490px; padding: 70px 52px; }
.atlas-feature-tag { font-size: 9px; letter-spacing: .23em; border: 1px solid #ffffff66; padding: 8px 12px; }
.atlas-feature-content h3 { font-family: Georgia, serif; font-size: clamp(65px, 7vw, 105px); font-weight: 400; line-height: .95; letter-spacing: -.055em; margin: 40px 0 24px; color: #fff; }
.atlas-feature-content h3 em { color: #d5dfa7; }
.atlas-feature-content p { max-width: 310px; font-size: 14px; line-height: 1.8; color: #e2e5dc; margin-bottom: 30px; }
.atlas-feature-credit { position: absolute; right: 32px; bottom: 30px; font-size: 9px; letter-spacing: .17em; color: #fff; }
.atlas-discovery { padding-top: 100px; padding-bottom: 80px; }
.atlas-discovery-heading { display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; margin: 34px 0; }
.atlas-discovery-heading h2 { font-family: Georgia, serif; font-size: clamp(46px, 5.5vw, 76px); font-weight: 400; line-height: 1.03; letter-spacing: -.05em; scroll-margin-top: 90px; }
.atlas-discovery-heading h2 em { color: var(--accent); }
.atlas-discovery-heading p { font-size: 13px; line-height: 1.8; color: var(--muted); margin: 0 0 7px; }
.atlas-search { display: flex; align-items: center; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); margin-bottom: 28px; padding: 12px 0; gap: 18px; }
.atlas-search-field { display: flex; align-items: center; flex: 1; gap: 14px; min-width: 0; }
.atlas-search-field .v-icon { color: var(--muted); }
.atlas-search input { width: 100%; min-width: 0; padding: 10px 0; font-size: 14px; color: var(--ink); }
.atlas-search input::placeholder { color: var(--muted); opacity: 1; }
.atlas-search-submit { display: flex; align-items: center; gap: 18px; font-size: 12px; padding: 10px; color: var(--ink); }
.atlas-search-submit span { font-size: 22px; }
.atlas-sort { border-left: 1px solid var(--line); padding-left: 24px; }
.atlas-sort select { font-size: 12px; color: var(--ink); padding: 10px 20px 10px 4px; appearance: auto; }
.atlas-sort option { background: var(--paper); }
.atlas-result-note { display: flex; justify-content: space-between; gap: 12px; margin: 0 0 24px; font-size: 9px; letter-spacing: .12em; color: var(--muted); overflow-wrap: anywhere; }
.atlas-gallery { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; align-items: start; }
.atlas-person { position: relative; display: block; height: 490px; overflow: hidden; background: #343c37; color: #fff !important; text-decoration: none; }
.atlas-person-image { position: absolute; inset: 0; transition: transform .7s ease; }
.atlas-person:hover .atlas-person-image { transform: scale(1.04); }
.atlas-person-symbolic { background: var(--poster-color); }
.atlas-person-symbolic .atlas-person-image { mix-blend-mode: luminosity; opacity: .36; }
.atlas-person-initials { position: absolute; inset: 10% 0 auto; text-align: center; font-family: Georgia, serif; font-size: clamp(80px, 9vw, 140px); line-height: 1.2; letter-spacing: -.09em; color: #ffffffbd; }
.atlas-person-gradient { position: absolute; inset: 0; background: linear-gradient(0deg, #10201aee, #14251c88 30%, #10201a00 73%); }
.atlas-person-number { position: absolute; left: 24px; top: 22px; font-size: 10px; letter-spacing: .16em; text-shadow: 0 1px 5px #000; }
.atlas-person-open { position: absolute; top: 16px; right: 16px; display: grid; place-items: center; width: 38px; height: 38px; border-radius: 50%; background: #f4f1e9; color: #252925; font-size: 24px; }
.atlas-person-copy { position: absolute; inset: auto 26px 30px; }
.atlas-person-eyebrow { font-size: 8px; font-weight: 600; letter-spacing: .16em; color: #d4ddce; }
.atlas-person-copy h3 { font-family: Georgia, serif; font-size: clamp(25px, 2.5vw, 37px); line-height: 1.12; font-weight: 400; letter-spacing: -.025em; margin: 12px 0 10px; color: #fff; overflow-wrap: anywhere; }
.atlas-person-handle { font-size: 11px; color: #e3e6dc; overflow-wrap: anywhere; }
.atlas-person-copy p { font-size: 12px; line-height: 1.6; margin: 14px 0 0; color: #edf0e9; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.atlas-pagination { display: flex; justify-content: space-between; gap: 18px; align-items: center; padding: 30px 0 0; margin-top: 28px; border-top: 1px solid var(--line); }
.atlas-pagination button { display: flex; align-items: center; gap: 12px; font-size: 12px; color: var(--ink); padding: 10px 0; }
.atlas-pagination > span { font-family: Georgia, serif; font-size: 28px; }
.atlas-pagination button:disabled, .atlas-search-submit:disabled { opacity: .4; cursor: default; }
.atlas-loading { min-height: 400px; display: flex; gap: 16px; align-items: center; justify-content: center; font-family: Georgia, serif; font-size: 24px; }
.atlas-state { padding: 70px 0; }
.atlas-state h3 { font-family: Georgia, serif; font-size: 40px; font-weight: 400; margin-bottom: 16px; }
.atlas-state p { margin-bottom: 24px; }
.atlas-closing { position: relative; overflow: hidden; background: #d8dfc7; padding: 68px 0; color: #263326; }
.atlas-closing .atlas-shell { position: relative; }
.atlas-closing .atlas-kicker { color: #53614d; }
.atlas-closing p { position: relative; z-index: 1; font-family: Georgia, serif; font-size: clamp(34px, 4vw, 57px); line-height: 1.15; letter-spacing: -.035em; margin: 20px 0 28px; }
.atlas-closing a { position: relative; z-index: 1; color: #263326; font-size: 12px; text-decoration: none; padding-bottom: 8px; border-bottom: 1px solid #263326; }
.atlas-closing a span { padding-left: 28px; }
.atlas-closing-art { position: absolute; right: 25px; top: -115px; font-size: 400px; line-height: 1; color: #bdcaa7; }
.atlas-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
.life-atlas a:focus-visible, .life-atlas button:focus-visible, .life-atlas input:focus-visible, .life-atlas select:focus-visible { outline: 3px solid #a581e2; outline-offset: 5px; }
.theme--dark .life-atlas { --paper: #202722; --ink: #eeeee3; --muted: #b5bdb0; --line: #444d44; --accent: #c6abe9; }
@media (min-width: 1550px) { .atlas-hero h1 { font-size: 118px; } }
@media (max-width: 1100px) {
  .atlas-shell { width: calc(100% - 56px); }
  .atlas-hero { min-height: 600px; gap: 20px; }
  .atlas-collage { height: 420px; }
  .atlas-stamp { width: 96px; height: 96px; }
  .atlas-feature-content { padding-left: 35px; }
  .atlas-person { height: 420px; }
  .atlas-person-copy { left: 20px; right: 20px; bottom: 24px; }
}
@media (max-width: 760px) {
  .atlas-shell { width: calc(100% - 40px); }
  .atlas-topline { padding-top: 54px; gap: 12px; }
  .atlas-wordmark { font-size: 9px; letter-spacing: .1em; }
  .atlas-wordmark .atlas-spark { font-size: 24px; }
  .atlas-hero { grid-template-columns: 1fr; padding-top: 42px; padding-bottom: 35px; }
  .atlas-hero h1 { font-size: clamp(56px, 11.5vw, 86px); }
  .atlas-kicker { font-size: 8px; }
  .atlas-deck { font-size: 14px; max-width: 420px; }
  .desktop-break { display: none; }
  .atlas-hero-note { margin-top: 24px; }
  .atlas-collage { width: 90%; max-width: 440px; height: 420px; margin: 35px auto 0; }
  .atlas-ribbon { gap: 28px; justify-content: flex-start; padding-left: 22px; }
  .atlas-feature { padding-top: 48px; }
  .atlas-section-label { font-size: 8px; letter-spacing: .09em; gap: 12px; }
  .atlas-section-label span:last-child { text-align: right; }
  .atlas-feature-heading { padding: 24px 0; }
  .atlas-feature-heading h2 { font-size: 27px; }
  .atlas-outline-star { font-size: 32px; }
  .atlas-feature-story { min-height: 660px; }
  .atlas-feature-image { inset: 0; }
  .atlas-feature-shade { background: linear-gradient(0deg, #172620 0%, #172620e8 23%, #17262055 63%, #17262000 100%); }
  .atlas-feature-content { position: absolute; bottom: 0; padding: 32px 24px 54px; }
  .atlas-feature-content h3 { font-size: 75px; margin: 24px 0 18px; }
  .atlas-feature-content p { font-size: 13px; margin-bottom: 24px; }
  .atlas-feature-index { font-size: 54px; right: 20px; top: 20px; }
  .atlas-feature-credit { right: auto; left: 24px; bottom: 22px; font-size: 7px; }
  .atlas-discovery { padding-top: 60px; padding-bottom: 50px; }
  .atlas-discovery-heading { display: block; }
  .atlas-discovery-heading h2 { font-size: 56px; }
  .atlas-discovery-heading p { margin-top: 22px; }
  .atlas-search { flex-wrap: wrap; gap: 8px; }
  .atlas-search-field { flex-basis: calc(100% - 88px); }
  .atlas-search input { font-size: 16px; }
  .atlas-search-submit { padding-right: 0; }
  .atlas-sort { width: 100%; border-left: 0; border-top: 1px solid var(--line); padding: 8px 0 0; }
  .atlas-sort select { width: 100%; font-size: 16px; }
  .atlas-gallery { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
  .atlas-person { height: 380px; margin-top: 0; }
  .atlas-person-copy h3 { font-size: 27px; }
  .atlas-person-initials { font-size: 90px; }
  .atlas-pagination { gap: 10px; }
  .atlas-pagination button { font-size: 10px; gap: 7px; }
  .atlas-closing { padding: 50px 0; }
  .atlas-closing-art { right: -100px; top: -20px; font-size: 300px; }
}
@media (max-width: 480px) {
  .atlas-gallery { grid-template-columns: 1fr; gap: 20px; }
  .atlas-person { height: 440px; }
  .atlas-person-copy { left: 26px; right: 26px; bottom: 30px; }
  .atlas-person-copy h3 { font-size: 35px; }
  .atlas-person-initials { font-size: 130px; }
  .atlas-collage { height: 350px; width: 94%; }
  .atlas-photo-memory { bottom: 11%; }
  .atlas-stamp { width: 88px; height: 88px; }
  .atlas-stamp span { font-size: 6px; }
  .atlas-stamp b { font-size: 39px; }
  .atlas-collage-foot { font-size: 7px; }
  .atlas-feature-heading h2 { font-size: 24px; }
  .atlas-feature-content h3 { font-size: 70px; }
  .atlas-result-note { font-size: 8px; }
}
@media (prefers-reduced-motion: reduce) { .atlas-person-image, .atlas-button { transition: none; } .atlas-person:hover .atlas-person-image, .atlas-button:hover { transform: none; } }
</style>
